import { 
  Calculator, 
  Package, 
  ClipboardList, 
  Receipt as ReceiptIcon, 
  Users, 
  Building2, 
  Calendar, 
  TrendingUp, 
  Zap, 
  PenTool, 
  BarChart3,
  Globe,
  LucideIcon,
  Boxes,
  Stethoscope,
  Syringe,
  ShieldCheck
} from 'lucide-react';
import { ActiveView } from '../hooks/useErpPlaygroundState';

export type NicheCluster = 'care_estetica' | 'campo_obras' | 'producao_food' | 'projetos_tech' | 'saude_clinica' | 'geral';

export interface DashboardFeatureItem {
  id: ActiveView;
  title: string;
  subtitle: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  iconBorder: string;
  requiredTier: 'free' | 'pro' | 'premium' | 'enterprise';
  allowedClusters?: NicheCluster[];
  actionName: string;
  actionDesc: string;
  badge?: {
    text: string;
    variant: 'free' | 'pro' | 'premium' | 'active' | 'custom';
    customClass?: string;
  };
}

export const ALL_DASHBOARD_FEATURES: Record<string, DashboardFeatureItem> = {
  // Hub Unificado de Agenda — grade interna + link online + configuração em um só lugar
  agenda: {
    id: 'agenda',
    title: 'Agenda & Atendimentos',
    subtitle: 'Grade diária, link online, equipe e horários — tudo em um só lugar',
    icon: Calendar,
    iconBg: 'bg-teal-50',
    iconColor: 'text-teal-700',
    iconBorder: 'border-teal-100/80',
    requiredTier: 'premium',
    actionName: 'Hub de Agenda & Atendimentos',
    actionDesc: 'Gerencie a grade de atendimento, horários fixos, equipe, comissões e o link de agendamento online para clientes — tudo em um único painel.',
    badge: { text: 'PREMIUM', variant: 'premium' }
  },
  // Mantido apenas para compatibilidade de tipos (ActiveView 'agendar')
  agendar: {
    id: 'agendar',
    title: 'Agenda Online do Cliente',
    subtitle: 'Link direto para clientes escolherem serviços e horários',
    icon: Calendar,
    iconBg: 'bg-brand/10',
    iconColor: 'text-brand',
    iconBorder: 'border-brand/20',
    requiredTier: 'premium',
    actionName: 'Agenda Online do Cliente',
    actionDesc: 'Link direto para seus clientes escolherem serviços e horários disponíveis.',
    badge: { text: 'PREMIUM', variant: 'premium' }
  },
  clientes: {
    id: 'clientes',
    title: 'Meus Clientes',
    subtitle: 'Histórico completo, preferências e rotas no Maps',
    icon: Users,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-brand',
    iconBorder: 'border-emerald-100/80',
    requiredTier: 'premium',
    actionName: 'Cadastro e Histórico de Clientes',
    actionDesc: 'Mantenha o histórico de compras, endereços com rota no Google Maps e preferências de cada cliente.',
    badge: { text: 'PREMIUM', variant: 'premium' }
  },
  recibo: {
    id: 'recibo',
    title: 'Recibos PIX',
    subtitle: 'Comprovante com Chave PIX e QR Code Copia e Cola',
    icon: ReceiptIcon,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-700',
    iconBorder: 'border-emerald-100/80',
    requiredTier: 'premium',
    actionName: 'Recibos com QR Code PIX',
    actionDesc: 'Emita recibos profissionais com QR Code Copia e Cola na hora pelo WhatsApp.',
    badge: { text: 'PREMIUM', variant: 'premium' }
  },
  orcamento: {
    id: 'orcamento',
    title: 'Orçamentos Rápidos',
    subtitle: 'Cálculo inteligente de lucro e margem de ganho',
    icon: Calculator,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    iconBorder: 'border-blue-100/80',
    requiredTier: 'free',
    actionName: 'Orçamentos Rápidos',
    actionDesc: 'Cálculo inteligente de valor de venda e margem.',
    badge: { text: '5/5 livres', variant: 'free' }
  },
  cockpit: {
    id: 'cockpit',
    title: 'Painel Financeiro',
    subtitle: 'Custos fixos, seu salário e lucro líquido real',
    icon: BarChart3,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-700',
    iconBorder: 'border-blue-100/80',
    requiredTier: 'premium',
    actionName: 'Painel de Resultados da Sua Empresa',
    actionDesc: 'Veja em 1 tela quanto precisa faturar para cobrir custos, seu salário e o lucro líquido real.',
    badge: { text: 'FINANÇAS', variant: 'custom', customClass: 'bg-blue-50 text-blue-700 border border-blue-100' }
  },
  custos: {
    id: 'custos',
    title: 'Centro de Custos',
    subtitle: 'Insumos, materiais, receitas e valor da sua hora',
    icon: Package,
    iconBg: 'bg-indigo-50',
    iconColor: 'text-indigo-600',
    iconBorder: 'border-indigo-100/80',
    requiredTier: 'pro',
    actionName: 'Centro de Custos',
    actionDesc: 'Insumos, materiais, receitas, kits e valor da sua hora de trabalho.',
    badge: { text: 'PRO', variant: 'pro' }
  },
  os: {
    id: 'os',
    title: 'OS com Fotos',
    subtitle: 'Vistoria e laudos com fotos antes, durante e depois',
    icon: ClipboardList,
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-600',
    iconBorder: 'border-purple-100/80',
    requiredTier: 'premium',
    actionName: 'Ordens de Serviço com Vistoria em Fotos',
    actionDesc: 'Gere laudos fotográficos profissionais antes, durante e depois de cada trabalho.',
    badge: { text: 'PREMIUM', variant: 'premium' }
  },
  equipe: {
    id: 'equipe',
    title: 'Minha Equipe & Usuários',
    subtitle: 'Até 10 colaboradores inclusos, travas de margem e permissões',
    icon: Users,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-700',
    iconBorder: 'border-emerald-100/80',
    requiredTier: 'premium',
    actionName: 'Gestão de Equipe Multi-Usuário (Até 10 Membros)',
    actionDesc: 'Adicione até 10 colaboradores da sua empresa com travas e liberdades personalizadas. Proteja margens de lucro e dados financeiros confidenciais.',
    badge: { text: 'PREMIUM', variant: 'premium' }
  },
  assinatura: {
    id: 'assinatura',
    title: 'Assinatura do Cliente',
    subtitle: 'Aceite e assinatura digital direto na tela do celular',
    icon: PenTool,
    iconBg: 'bg-orange-50',
    iconColor: 'text-orange-600',
    iconBorder: 'border-orange-100/80',
    requiredTier: 'premium',
    actionName: 'Assinatura do Cliente',
    actionDesc: 'Colha a assinatura ou confirmação do cliente direto no celular nas suas ordens de serviço.',
    badge: { text: 'PREMIUM', variant: 'premium' }
  },
  fornecedores: {
    id: 'fornecedores',
    title: 'Fornecedores',
    subtitle: 'Busca CNPJ na Receita Federal e importação de notas',
    icon: Building2,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-700',
    iconBorder: 'border-blue-100/80',
    requiredTier: 'premium',
    actionName: 'Gestão de Fornecedores & Compras',
    actionDesc: 'Consulte dados de empresas por CNPJ da Receita Federal e auto-cadastre fornecedores a partir da Nota Fiscal.',
    badge: { text: 'PREMIUM', variant: 'premium' }
  },
  gestao: {
    id: 'gestao',
    title: 'Fluxo de Vendas',
    subtitle: 'Propostas aprovadas, faturamento e vendas do mês',
    icon: TrendingUp,
    iconBg: 'bg-rose-50',
    iconColor: 'text-rose-600',
    iconBorder: 'border-rose-100/80',
    requiredTier: 'premium',
    actionName: 'Fluxo de Vendas e Controle Financeiro',
    actionDesc: 'Acompanhe suas vendas em andamento, propostas aprovadas e faturamento previsto.',
    badge: { text: 'PREMIUM', variant: 'premium' }
  },
  radar: {
    id: 'radar',
    title: 'Preços da Região',
    subtitle: 'Média local de preços e argumentos para valorizar seu serviço',
    icon: TrendingUp,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-brand',
    iconBorder: 'border-emerald-100/80',
    requiredTier: 'premium',
    actionName: 'Comparação com o Mercado da Sua Região',
    actionDesc: 'Compare seus preços com a média da sua cidade e veja argumentos para valorizar seu trabalho.',
    badge: { text: 'RADAR', variant: 'custom', customClass: 'bg-emerald-50 text-brand border border-emerald-100' }
  },
  estoque_hub: {
    id: 'estoque_hub',
    title: 'Estoque & Lotes',
    subtitle: 'Balancete financeiro, lotes, validade e extrato de movimentos',
    icon: Boxes,
    iconBg: 'bg-indigo-50',
    iconColor: 'text-indigo-700',
    iconBorder: 'border-indigo-100/80',
    requiredTier: 'enterprise',
    actionName: 'Gestão Completa de Estoque & Lotes',
    actionDesc: 'Balancete financeiro de posição de estoque, controle rigoroso de lotes com vencimento e extrato analítico estilo conta corrente.',
    badge: { text: 'PLANO EMPRESA', variant: 'custom', customClass: 'bg-indigo-50 text-indigo-700 border border-indigo-200' }
  },
  prontuario_soap: {
    id: 'prontuario_soap',
    title: 'Prontuário SOAP & CRM',
    subtitle: 'Histórico clínico legal, sinais vitais e checkout integrado',
    icon: Stethoscope,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-700',
    iconBorder: 'border-blue-100/80',
    requiredTier: 'enterprise',
    allowedClusters: ['saude_clinica'],
    actionName: 'Prontuário Eletrônico SOAP & Ficha Clínica',
    actionDesc: 'Prontuário com valor jurídico inviolável, sinais vitais, hipótese diagnóstica, prescrições e baixa automática de estoque no checkout.',
    badge: { text: 'PLANO EMPRESA', variant: 'custom', customClass: 'bg-blue-50 text-blue-700 border border-blue-200' }
  },
  retorno_vacinas: {
    id: 'retorno_vacinas',
    title: 'Central de Retorno de Vacinas',
    subtitle: 'Lembretes de retornos no WhatsApp com Deep Link da agenda',
    icon: Syringe,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-700',
    iconBorder: 'border-emerald-100/80',
    requiredTier: 'enterprise',
    allowedClusters: ['saude_clinica'],
    actionName: 'Central de Retorno de Vacinas no WhatsApp',
    actionDesc: 'Painel operacional com listas de vacinas vencidas e a vencer, com disparo via Deep Link do WhatsApp contendo o link da sua agenda online.',
    badge: { text: 'PLANO EMPRESA', variant: 'custom', customClass: 'bg-emerald-50 text-emerald-700 border border-emerald-200' }
  },
  cofre_exames: {
    id: 'cofre_exames',
    title: 'Cofre de Exames & DICOM',
    subtitle: 'Armazenamento seguro R2 com guarda de 5 anos e visualizador',
    icon: ShieldCheck,
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-700',
    iconBorder: 'border-purple-100/80',
    requiredTier: 'enterprise',
    allowedClusters: ['saude_clinica'],
    actionName: 'Cofre de Exames e Imagens DICOM',
    actionDesc: 'Guarda legal de 5 anos no Cloudflare R2 com compactação client-side WebP e visualizador de raio-x com janela óssea e régua milimétrica.',
    badge: { text: 'PLANO EMPRESA', variant: 'custom', customClass: 'bg-purple-50 text-purple-700 border border-purple-200' }
  },
  portal_contador: {
    id: 'portal_contador',
    title: 'Portal do Contador',
    subtitle: 'Fechamento fiscal, livro caixa analítico e exportação Domínio/Alterdata',
    icon: Building2,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-700',
    iconBorder: 'border-blue-100/80',
    requiredTier: 'enterprise',
    actionName: 'Portal do Contador & Fechamento Fiscal',
    actionDesc: 'Acesso dedicado para o escritório de contabilidade com conformidade LGPD, livro caixa e arquivos para importação direta no software contábil.',
    badge: { text: 'PLANO EMPRESA', variant: 'custom', customClass: 'bg-blue-50 text-blue-700 border border-blue-200' }
  }
};

export interface ClusterConfig {
  cluster: NicheCluster;
  clusterName: string;
  badgeLabel: string;
  featuredFeatureIds: string[];
  secondaryFeatureIds: string[];
  customLabels?: Record<string, { title?: string; subtitle?: string }>;
}

export const CLUSTER_CONFIGS: Record<NicheCluster, ClusterConfig> = {
  // ─── BELEZA, SALÃO & ESTÉTICA ───────────────────────────────────────────────
  // Agenda é a ferramenta #1 do dia a dia — grade, equipe, link online e horários fixos
  care_estetica: {
    cluster: 'care_estetica',
    clusterName: 'Beleza, Salão & Estética',
    badgeLabel: 'Salão & Estética',
    featuredFeatureIds: ['agenda', 'clientes', 'recibo', 'orcamento', 'cockpit'],
    secondaryFeatureIds: ['custos', 'gestao', 'radar', 'assinatura', 'fornecedores', 'os', 'estoque_hub'],

    customLabels: {
      agenda: {
        title: 'Grade do Salão & Equipe',
        subtitle: 'Multi-atendentes, horários fixos, link online e comissões'
      },
      orcamento: {
        title: 'Serviços & Atendimentos',
        subtitle: 'Orçamento rápido de procedimentos e combos'
      },
      clientes: {
        title: 'Clientes & Fidelidade',
        subtitle: 'Histórico de procedimentos e preferências'
      }
    }
  },

  // ─── OBRAS, MANUTENÇÃO & SERVIÇOS TÉCNICOS ──────────────────────────────────
  campo_obras: {
    cluster: 'campo_obras',
    clusterName: 'Obras, Manutenção & Serviços Técnicos',
    badgeLabel: 'Técnico & Obras',
    featuredFeatureIds: ['orcamento', 'os', 'assinatura', 'custos', 'recibo', 'fornecedores'],
    secondaryFeatureIds: ['clientes', 'gestao', 'cockpit', 'radar', 'agenda'],

    customLabels: {
      orcamento: {
        title: 'Orçamentos de Obra/Serviço',
        subtitle: 'Mão de obra e materiais com cálculo de margem'
      },
      os: {
        title: 'OS com Fotos de Vistoria',
        subtitle: 'Laudos antes, durante e depois com assinatura'
      },
      agenda: {
        title: 'Agenda de Visitas & Serviços',
        subtitle: 'Organize visitas técnicas, retornos e manutenções'
      }
    }
  },

  // ─── ALIMENTAÇÃO & GASTRONOMIA ───────────────────────────────────────────────
  producao_food: {
    cluster: 'producao_food',
    clusterName: 'Alimentação & Gastronomia',
    badgeLabel: 'Gastronomia',
    featuredFeatureIds: ['custos', 'orcamento', 'clientes', 'recibo', 'cockpit'],
    secondaryFeatureIds: ['gestao', 'fornecedores', 'radar', 'agenda', 'assinatura', 'os'],

    customLabels: {
      custos: {
        title: 'Fichas Técnicas & Insumos',
        subtitle: 'Cálculo de receitas, ingredientes e rendimento'
      },
      orcamento: {
        title: 'Encomendas & Pedidos',
        subtitle: 'Orçamento de bolos, doces e combos com lucro certo'
      },
      agenda: {
        title: 'Agenda de Entregas & Retiradas',
        subtitle: 'Datas de entrega, retiradas e capacidade diária'
      }
    }
  },

  // ─── CRIATIVO, TECNOLOGIA & CONSULTORIA ─────────────────────────────────────
  projetos_tech: {
    cluster: 'projetos_tech',
    clusterName: 'Criativo, Tecnologia & Consultoria',
    badgeLabel: 'Criativo & Tech',
    featuredFeatureIds: ['orcamento', 'clientes', 'gestao', 'recibo', 'cockpit', 'radar'],
    secondaryFeatureIds: ['custos', 'assinatura', 'agenda', 'fornecedores', 'os'],

    customLabels: {
      orcamento: {
        title: 'Propostas Comerciais',
        subtitle: 'Valor/hora, escopo de projeto e entregáveis'
      },
      gestao: {
        title: 'Pipeline de Projetos',
        subtitle: 'Quadro de propostas enviadas e em andamento'
      },
      agenda: {
        title: 'Agenda de Consultas & Reuniões',
        subtitle: 'Sessões, consultas e reuniões com link online'
      }
    }
  },

  // ─── SAÚDE, CLÍNICA MÉDICA & VETERINÁRIA ──────────────────────────────────
  saude_clinica: {
    cluster: 'saude_clinica',
    clusterName: 'Saúde, Clínica Médica & Veterinária',
    badgeLabel: 'Saúde & Clínica',
    featuredFeatureIds: ['prontuario_soap', 'retorno_vacinas', 'estoque_hub', 'agenda', 'recibo', 'clientes'],
    secondaryFeatureIds: ['portal_contador', 'cofre_exames', 'orcamento', 'cockpit', 'custos', 'fornecedores', 'assinatura', 'radar'],
    customLabels: {
      prontuario_soap: {
        title: 'Prontuário SOAP & CRM',
        subtitle: 'Anamnese, sinais vitais, hipótese e conduta com valor jurídico'
      },
      retorno_vacinas: {
        title: 'Central de Retorno de Vacinas',
        subtitle: 'Lembretes e reforços via WhatsApp sem custo de API'
      },
      estoque_hub: {
        title: 'Estoque & Controle de Lotes',
        subtitle: 'Balancete financeiro, validade e baixa FIFO automática'
      }
    }
  },

  // ─── GERAL ───────────────────────────────────────────────────────────────────
  geral: {
    cluster: 'geral',
    clusterName: 'Gestão Geral do Negócio',
    badgeLabel: 'Profissional',
    featuredFeatureIds: ['orcamento', 'agenda', 'clientes', 'recibo', 'cockpit'],
    secondaryFeatureIds: ['os', 'fornecedores', 'custos', 'gestao', 'assinatura', 'radar', 'estoque_hub']
  }
};

/**
 * Detecta o cluster de nicho a partir do texto do nicho/profissão
 */
export function detectNicheCluster(niche?: string, profession?: string): NicheCluster {
  const combined = `${niche || ''} ${profession || ''}`.toLowerCase();

  // 0. Saúde, Clínica Médica, Veterinária, Odonto
  if (
    combined.includes('vet') ||
    combined.includes('pet') ||
    combined.includes('animal') ||
    combined.includes('canil') ||
    combined.includes('gato') ||
    combined.includes('cachorro') ||
    combined.includes('clinica') ||
    combined.includes('clínica') ||
    combined.includes('medic') ||
    combined.includes('médic') ||
    combined.includes('saude') ||
    combined.includes('saúde') ||
    combined.includes('odonto') ||
    combined.includes('dentist') ||
    combined.includes('consultorio') ||
    combined.includes('consultório') ||
    combined.includes('hospital')
  ) {
    return 'saude_clinica';
  }

  // 1. Beleza, Salão, Manicure, Estética
  if (
    combined.includes('cabel') ||
    combined.includes('manicur') ||
    combined.includes('unha') ||
    combined.includes('pedicur') ||
    combined.includes('salao') ||
    combined.includes('salão') ||
    combined.includes('barbeir') ||
    combined.includes('barbearia') ||
    combined.includes('estet') ||
    combined.includes('beleza') ||
    combined.includes('lash') ||
    combined.includes('cilio') ||
    combined.includes('sobrancelha') ||
    combined.includes('massag') ||
    combined.includes('massoterap') ||
    combined.includes('terapeuta') ||
    combined.includes('visagism') ||
    combined.includes('podolog') ||
    combined.includes('depila') ||
    combined.includes('maquiag') ||
    combined.includes('make')
  ) {
    return 'care_estetica';
  }

  // 2. Obras, Instalações, Mecânica, Campo
  if (
    combined.includes('eletric') ||
    combined.includes('encan') ||
    combined.includes('hidraul') ||
    combined.includes('ar cond') ||
    combined.includes('hvac') ||
    combined.includes('refrig') ||
    combined.includes('mecanic') ||
    combined.includes('auto') ||
    combined.includes('marmor') ||
    combined.includes('marcen') ||
    combined.includes('serralh') ||
    combined.includes('obra') ||
    combined.includes('reforma') ||
    combined.includes('pintor') ||
    combined.includes('pintura') ||
    combined.includes('pedreir') ||
    combined.includes('gesso') ||
    combined.includes('chaveir') ||
    combined.includes('vidrac') ||
    combined.includes('funilar')
  ) {
    return 'campo_obras';
  }

  // 3. Gastronomia & Alimentação
  if (
    combined.includes('confeit') ||
    combined.includes('doce') ||
    combined.includes('bolo') ||
    combined.includes('marmit') ||
    combined.includes('salgad') ||
    combined.includes('pao') ||
    combined.includes('pão') ||
    combined.includes('padar') ||
    combined.includes('gastronom') ||
    combined.includes('comida') ||
    combined.includes('buffet') ||
    combined.includes('chef') ||
    combined.includes('cozinha') ||
    combined.includes('alimenta')
  ) {
    return 'producao_food';
  }

  // 4. Criativo, Tech, Consultoria
  if (
    combined.includes('design') ||
    combined.includes('dev') ||
    combined.includes('program') ||
    combined.includes('softwar') ||
    combined.includes('ti ') ||
    combined.includes('tech') ||
    combined.includes('foto') ||
    combined.includes('video') ||
    combined.includes('áudio') ||
    combined.includes('social media') ||
    combined.includes('market') ||
    combined.includes('consultor') ||
    combined.includes('advog') ||
    combined.includes('psicolog') ||
    combined.includes('coach')
  ) {
    return 'projetos_tech';
  }

  return 'geral';
}

export function getDashboardConfigForNiche(niche?: string, profession?: string): ClusterConfig {
  const cluster = detectNicheCluster(niche, profession);
  return CLUSTER_CONFIGS[cluster];
}

/**
 * Ajusta dinamicamente badges de recursos conforme o plano e a cota do usuário.
 */
export function getDynamicFeatureItem(
  feature: DashboardFeatureItem,
  planTier: 'free' | 'pro' | 'premium' | 'enterprise',
  quotesCount: number = 0,
  maxFreeQuotes: number = 5
): DashboardFeatureItem {
  if (!feature) return feature;
  if (feature.id !== 'orcamento') return feature;

  if (planTier === 'pro' || planTier === 'premium' || planTier === 'enterprise') {
    return {
      ...feature,
      badge: {
        text: 'ILIMITADO',
        variant: 'custom',
        customClass: 'bg-emerald-50 text-emerald-700 border border-emerald-100'
      }
    };
  }

  const effectiveMax = maxFreeQuotes > 0 ? maxFreeQuotes : 5;
  const remaining = Math.max(0, effectiveMax - quotesCount);
  const isZero = remaining === 0;

  return {
    ...feature,
    badge: {
      text: isZero ? `0/${effectiveMax} livres` : `${remaining}/${effectiveMax} ${remaining === 1 ? 'livre' : 'livres'}`,
      variant: 'custom',
      customClass: isZero
        ? 'bg-rose-50 text-rose-700 border border-rose-200'
        : remaining <= 1
        ? 'bg-amber-50 text-amber-800 border border-amber-200'
        : 'bg-zinc-100 text-zinc-600 border border-zinc-200'
    }
  };
}

export type DashboardCategoryId = 'service' | 'sales' | 'finance' | 'management' | 'enterprise';

export interface DashboardCategoryMeta {
  id: DashboardCategoryId;
  title: string;
  subtitle: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  iconBorder: string;
  featureIds: string[];
}

export const DASHBOARD_CATEGORIES: DashboardCategoryMeta[] = [
  {
    id: 'service',
    title: 'Atendimento & Clientes',
    subtitle: 'Agenda, atendimentos, histórico de clientes e assinatura',
    icon: Calendar,
    iconBg: 'bg-teal-50',
    iconColor: 'text-teal-700',
    iconBorder: 'border-teal-200/80',
    featureIds: ['agenda', 'agendar', 'clientes', 'assinatura']
  },
  {
    id: 'sales',
    title: 'Orçamentos & Ordens de Serviço',
    subtitle: 'Elaboração de orçamentos, ordens de serviço e laudos',
    icon: Calculator,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-700',
    iconBorder: 'border-blue-200/80',
    featureIds: ['orcamento', 'os']
  },
  {
    id: 'finance',
    title: 'Financeiro, Vendas & Lucro',
    subtitle: 'Recibos PIX, faturamento, custos fixos e radar de mercado',
    icon: ReceiptIcon,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-700',
    iconBorder: 'border-emerald-200/80',
    featureIds: ['recibo', 'cockpit', 'gestao', 'radar']
  },
  {
    id: 'management',
    title: 'Gestão da Empresa & Equipe',
    subtitle: 'Centro de custos, fornecedores e membros da equipe',
    icon: Building2,
    iconBg: 'bg-indigo-50',
    iconColor: 'text-indigo-700',
    iconBorder: 'border-indigo-200/80',
    featureIds: ['custos', 'fornecedores', 'equipe']
  },
  {
    id: 'enterprise',
    title: 'Módulos Corporativos (Plano Empresa)',
    subtitle: 'Estoque por lotes, fechamento contábil e recursos empresariais',
    icon: Boxes,
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-700',
    iconBorder: 'border-purple-200/80',
    featureIds: ['estoque_hub', 'portal_contador', 'retorno_vacinas', 'prontuario_soap', 'cofre_exames']
  }
];

export function getDefaultCategoryForCluster(cluster: NicheCluster): DashboardCategoryId {
  switch (cluster) {
    case 'care_estetica':
    case 'saude_clinica':
      return 'service';
    case 'campo_obras':
    case 'projetos_tech':
      return 'sales';
    case 'producao_food':
      return 'finance';
    default:
      return 'sales';
  }
}

